import { useEffect, useRef } from "react";
import { BUILD_VERSION } from "@/version";

/* Auto-reload-on-new-deploy.
 *
 * The frozen BUILD_VERSION is baked into this JS bundle at build time
 * (see scripts/stamp_build_version.js). At runtime we periodically
 * fetch /version.json · the same stamp, served as a static file that
 * Cloudflare re-fetches on every deploy. If the two diverge, a new
 * build is live but the user is on the old one · show a brief toast
 * then hard-reload so they pick up the fresh HTML + hashed chunks.
 *
 * Triggers a check on:
 *   • initial mount
 *   • every tab refocus (visibilitychange)
 *   • every 60 s while the tab is visible
 *
 * The fetch always includes a `t=<now>` query param so neither the
 * browser nor Cloudflare can hand us a stale copy of version.json. */
const POLL_MS = 60_000;

export default function VersionWatcher() {
  const reloadingRef = useRef(false);

  useEffect(() => {
    let timer;

    const reloadIfStale = (latest) => {
      if (reloadingRef.current) return;
      if (!latest || latest === BUILD_VERSION) return;
      // Loop guard: if we already triggered a reload in the last
      // 30 s and STILL see a mismatch, something else is wrong (likely
      // an edge cache holding the old HTML) · stay put, don't loop.
      const lastReload = parseInt(
        sessionStorage.getItem("ofin.lastVersionReload") || "0",
        10,
      );
      if (Date.now() - lastReload < 30_000) return;
      reloadingRef.current = true;
      try {
        sessionStorage.setItem(
          "ofin.lastVersionReload",
          Date.now().toString(),
        );
      } catch (_e) {
        /* private mode · fine */
      }
      // Silent hard-reload · no toast, no version disclosure. Adding
      // a cache-buster query param ensures the next HTML fetch is
      // fresh even if some intermediate cache is misbehaving.
      const url = new URL(window.location.href);
      url.searchParams.set("v", Date.now().toString(36));
      window.location.replace(url.toString());
    };

    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: "no-store",
          credentials: "omit",
        });
        if (!res.ok) return;
        const data = await res.json();
        reloadIfStale(data?.build);
      } catch (_e) {
        /* offline / network blip · ignore, we'll retry on next tick */
      }
    };

    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    timer = setInterval(check, POLL_MS);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return null;
}
